import React, { useState } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { insertAuditLog } from '../../../services/auditLogService';

const SetTempPasswordModal = ({ isOpen, user, onClose }) => {
  const [tempPassword, setTempPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleClose = () => {
    setTempPassword('');
    setConfirmPassword('');
    setError('');
    setSuccess(false);
    setLoading(false);
    onClose();
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    if (tempPassword?.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (tempPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const { data, error: fnError } = await supabase?.functions?.invoke('admin-user-actions', {
        body: {
          action: 'set_temp_password',
          userId: user?.id,
          tempPassword,
        },
      });

      if (fnError) throw fnError;
      if (!data?.success) throw new Error(data?.error || 'Failed to set temporary password');

      await insertAuditLog({
        actionType: 'Temp Password Set',
        affectedUserId: user?.id,
        affectedUserEmail: user?.email,
        beforeValue: null,
        afterValue: { must_change_password: true },
        description: `Admin set a temporary password for ${user?.full_name || user?.email}. User will be prompted to change it on next login.`,
        metadata: {},
      });

      setSuccess(true);
    } catch (err) {
      console.error('Set temp password error:', err?.message);
      setError(err?.message || 'Failed to set temporary password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !user) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
      <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="border-b border-border px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <Icon name="KeyRound" size={20} className="text-amber-600" />
            </div>
            <h2 className="text-xl font-semibold text-foreground">Set Temporary Password</h2>
          </div>
          <Button variant="ghost" size="icon" onClick={handleClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        <div className="p-6 space-y-4">
          {/* User info */}
          <div className="bg-muted/50 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                <Icon name="User" size={18} className="text-accent" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">{user?.full_name || 'Unnamed User'}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
              </div>
            </div>
          </div>

          {success ? (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
                <Icon name="CheckCircle" size={20} className="text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-green-800">Temporary password set successfully</p>
                  <p className="text-xs text-green-700 mt-1">
                    {user?.full_name || user?.email} will be prompted to change their password the next time they log in.
                  </p>
                </div>
              </div>
              <Button variant="default" onClick={handleClose} className="w-full">
                Done
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-xs text-amber-800">
                  <strong>Note:</strong> The user will be required to change this password immediately after logging in.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Temporary Password</label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={tempPassword}
                    onChange={(e) => setTempPassword(e?.target?.value)}
                    placeholder="Min. 8 characters"
                    required
                    disabled={loading}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={16} />
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Confirm Password</label>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e?.target?.value)}
                  placeholder="Re-enter password"
                  required
                  disabled={loading}
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={handleClose} className="flex-1" disabled={loading}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="default"
                  iconName="KeyRound"
                  iconPosition="left"
                  className="flex-1"
                  loading={loading}
                  disabled={loading}
                >
                  Set Password
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default SetTempPasswordModal;
